<template>
	<div
		class="col-12 cadastro-grid-shell"
		v-altura-disponivel="fitViewport"
		:class="{ 'cadastro-grid-keyboard': keyboardNavigation, 'cadastro-grid-display-only': !selectionEnabled }"
		:tabindex="keyboardNavigation && selectionEnabled ? 0 : null"
		v-if="temLinhas || emptyText"
		@keydown="tratarNavegacaoTeclado"
		@pointerdown="navegacaoTecladoAtiva = false"
	>
        <grid-layout-editor v-if="gridLayoutEditorOpen" :key="gridLayoutContextKey || 'session'" :title="title" :columns="columns" :rows="linhasOrdenadas" :default-totals="gridDefaultTotals" :library="gridLayoutLibrary" :initial-sort="gridInitialSort" :current-sort="ordenacao" :source-element="$el" :user-id="gridLayoutUserId" :manager="gridLayoutManager" :users="gridLayoutUsers" :users-loading="gridLayoutUsersLoading" :users-error="gridLayoutUsersError" :access-unavailable="gridLayoutAccessInvalid" @reload-users="loadGridLayoutUsers" :warning="gridLayoutWarning" :error="gridLayoutError" @cancel="gridLayoutEditorOpen = false" @apply="saveGridLayouts" />
		<div class="card cadastro-table-card mb-0">
			<div class="card-body p-0">
				<div class="table-responsive cadastro-table-scroll" v-if="temLinhas">
					<table class="table table-hover align-middle cadastro-table mb-0">
						<thead>
							<tr>
								<th v-if="selectionEnabled" class="cadastro-table-check text-center">
									<input
										class="form-check-input cadastro-select-checkbox"
										type="checkbox"
										:checked="singleSelect ? false : todosSelecionados"
										:indeterminate.prop="singleSelect ? quantidadeSelecionada > 0 : algunsSelecionados"
										:title="singleSelect ? 'Limpar seleção' : 'Selecionar todos'"
										aria-label="Selecionar ou limpar registros desta página" :disabled="confirmandoSelecao" @change="alternarTodos($event)"
									/>
								</th>
								<th
									v-for="(coluna, colunaIndex) in colunasVisiveis"
									:key="coluna.key"
									:class="classeCabecalho(coluna, colunaIndex)"
                                    
									@click="ordenar(coluna)"
								>
									{{ coluna.label }}
									<i v-if="coluna.editable" class="far fa-pen ms-1" title="Campo editável" aria-label="Campo editável"></i>
									<i v-if="coluna.sortable !== false" :class="iconeOrdenacao(coluna)" class="font-10 ms-1"></i>
								</th>
							</tr>
						</thead>
						<tbody>
							<template v-for="(item, index) in linhasOrdenadas">
								<tr
									:key="chaveLinha(item, index)"
									class="cadastro-table-row"
									:class="[{ 'cadastro-table-row-alt': index % 2 === 1, 'table-active': selectionEnabled && itemSelecionado(item, index), 'cadastro-keyboard-active': selectionEnabled && keyboardNavigation && navegacaoTecladoAtiva && indiceTeclado === index }, rowClass ? rowClass(item, index) : null]"
									@click="selectionEnabled && clicarItem(item, index)"
									@dblclick="selectionEnabled && executarAcaoPadrao(item, index)"
								>
									<td v-if="selectionEnabled" class="cadastro-table-check text-center" @click.stop @dblclick.stop>
										<input class="form-check-input cadastro-select-checkbox" type="checkbox" :checked="itemSelecionado(item, index)" :aria-label="`Selecionar registro ${index + 1}`" :disabled="confirmandoSelecao" @change="alternarItem(item, index, $event)" />
									</td>
									<td v-for="(coluna, colunaIndex) in colunasVisiveis" :key="`${chaveLinha(item, index)}-${coluna.key}`" :class="classeCelula(coluna, colunaIndex)" >
										<slot :name="`cell-${coluna.key}`" :row="item" :item="item" :index="index" :value="valorCelula(item, coluna)">
											{{ formatarValorCelula(item, coluna) }}
										</slot>
									</td>
								</tr>
								<tr v-if="deveExibirDetalhe(item, index)" :key="`${chaveLinha(item, index)}-detail`" class="cadastro-row-detail">
									<td :colspan="colunasVisiveis.length + (selectionEnabled ? 1 : 0)">
										<slot name="row-detail" :row="item" :item="item" :index="index" />
									</td>
								</tr>
							</template>
						</tbody>
					</table>
				</div>
				<div v-if="temTotalizadores" class="cadastro-totalizadores-dock" aria-label="Totalizadores da grade" title="Cálculos sobre os registros exibidos">
					<span class="cadastro-totalizadores-dock__label"><i class="far fa-sigma color-theme"></i>Totais<span v-if="totalRegistros > rows.length" class="ms-1">(página atual)</span></span>
					<span v-for="total in gridTotalizers" :key="total.id" class="cadastro-totalizadores-dock__item"><small>{{ total.label }}</small><strong>{{ total.formatted }}</strong></span>
				</div>

				<div v-if="!temLinhas" class="cadastro-table-scroll cadastro-table-empty w-100 py-5 text-center">
					<i class="fal fa-align-slash font-50 opacity-50 mb-4"></i>
					<p class="text-muted mb-0">{{ emptyText }}</p>
				</div>

			</div>
		</div>

		<div v-if="showActionBar && selectionEnabled" class="cadastro-table-enhance bg-theme text-white d-flex flex-wrap align-items-center justify-content-between gap-2">
			<div class="d-flex align-items-center flex-wrap gap-3">
				<span v-if="quantidadeSelecionada > 0" class="weight-500"><i class="fas fa-check-circle me-2"></i>{{ quantidadeSelecionada }} {{ quantidadeSelecionada === 1 ? 'item selecionado' : 'itens selecionados' }}</span>
				<span v-else class="weight-500"><i class="far fa-hand-pointer me-2"></i>Selecione um item para habilitar as ações</span>
				<span v-if="selecaoEmMassa" class="badge border border-white">Seleção em massa</span>
				<button type="button" class="btn btn-sm btn-link text-white text-decoration-none p-0" :disabled="quantidadeSelecionada === 0" @click="limparSelecao"><i class="far fa-times font-11 me-1"></i>Limpar selecao</button>
			</div>
			<div class="d-flex align-items-center flex-wrap gap-2">
				<button
					v-for="acao in acoesVisiveis"
					:key="acao.key"
					type="button"
					class="btn btn-sm"
					:class="classeBotaoAcao(acao)"
					:disabled="quantidadeSelecionada === 0 || (acao.single && quantidadeSelecionada !== 1) || !acaoHabilitada(acao)"
					@click="emitirAcao(acao.key)"
				>
					<i :class="acao.icon" class="font-11 me-1"></i>{{ acao.label }}
				</button>
			</div>
		</div>

		<div v-if="showFooter || gridConfigurable || mostrarAcoesSaida" class="cadastro-grid-summary-footer">
			<div v-if="showFooter || mostrarAcoesSaida" class="cadastro-grid-summary-text">
				<strong v-if="showFooter"><i class="far fa-table color-theme me-1"></i>{{ title }}</strong>
				<span v-if="showFooter"><i class="fas fa-angle-right color-theme me-1"></i>Exibindo {{ textoExibindoFinal }} de {{ totalRegistros }} registro(s)</span>
				<div v-if="mostrarAcoesSaida" class="cadastro-grid-footer-output-actions">
					<button type="button" class="btn btn-sm cadastro-grid-output-button cadastro-grid-output-button--excel" :disabled="!temLinhas" title="Exportar resultado filtrado para Excel" aria-label="Exportar resultado filtrado para Excel" @click="exportarExcel"><i class="far fa-file-excel"></i></button>
					<button type="button" class="btn btn-sm cadastro-grid-output-button cadastro-grid-output-button--print" :disabled="!temLinhas" title="Imprimir resultado filtrado" aria-label="Imprimir resultado filtrado" @click="imprimirGrid"><i class="far fa-print"></i></button>
				</div>
			</div>
			<div v-if="gridConfigurable || (showFooter && showPagination)" class="cadastro-grid-summary-pagination">
        <div v-if="gridConfigurable" class="cadastro-grid-layout-toolbar" data-editing-ignore @click.stop @keydown.stop>
            <span v-if="gridLayoutLibrary.active !== 'system'" class="cadastro-grid-layout-name" :title="gridLayoutName">{{ gridLayoutName }}</span>
            <button type="button" class="btn btn-sm btn-outline-secondary cadastro-grid-layout-button" aria-label="Colunas e layouts" @click="openGridLayouts"><i class="far fa-columns me-1" aria-hidden="true"></i>Colunas e layouts</button>
        </div>

				<div v-if="showFooter && showPagination" class="cadastro-pagination-size">
					<span>Itens:</span>
					<v-select class="cadastro-page-size-select" :value="size" :options="pageSizes" :searchable="false" :clearable="false" @input="$emit('size-change', Number($event))" />
				</div>
				<div v-if="showFooter && showPagination" class="cadastro-pagination-nav">
					<button type="button" class="btn btn-sm btn-outline-secondary" :disabled="page <= 0" @click="$emit('page-change', 0)" title="Primeira página"><i class="fas fa-angle-double-left"></i></button>
					<button type="button" class="btn btn-sm btn-outline-secondary" :disabled="page <= 0" @click="$emit('page-change', page - 1)" title="Página anterior"><i class="fas fa-angle-left"></i></button>
					<span class="cadastro-pagination-label">Pág. {{ page + 1 }}/{{ totalPaginas }}</span>
					<button type="button" class="btn btn-sm btn-outline-secondary" :disabled="page >= totalPaginas - 1" @click="$emit('page-change', page + 1)" title="Próxima página"><i class="fas fa-angle-right"></i></button>
					<button type="button" class="btn btn-sm btn-outline-secondary" :disabled="page >= totalPaginas - 1" @click="$emit('page-change', totalPaginas - 1)" title="Última página"><i class="fas fa-angle-double-right"></i></button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import Swal from 'sweetalert2'
import gridSelection from '@/shared/grids/selection'
import gridLayouts from '@/shared/grids/layoutMixin'
import { loadGridLayoutUsers } from '@/services/gridLayoutUsers'
import '@/shared/grids/layouts.css'
import '@/shared/grids/selection.css'
import alturaDisponivel from '@/directives/alturaDisponivel'

export default {
	name: 'CadastroGrid',
	mixins: [gridSelection(Swal), gridLayouts('cliente', loadGridLayoutUsers, { remote: true })],
	directives: { alturaDisponivel },
	props: {
		fitViewport: { type: Boolean, default: true },
		selectionEnabled: { type: Boolean, default: true },
		showFooter: { type: Boolean, default: true },
		rowClass: { type: Function, default: null },
		rows: { type: Array, default: () => [] },
		columns: { type: Array, default: () => [] },
		actions: { type: Array, default: () => [] },
		title: { type: String, default: 'Grade' },
		emptyText: { type: String, default: '' },
		rowKey: { type: [String, Function], default: 'id' },
		total: { type: Number, default: 0 },
		page: { type: Number, default: 0 },
		size: { type: Number, default: 20 },
		pageSizes: { type: Array, default: () => [10, 20, 50, 100] },
		showPagination: { type: Boolean, default: true },
		showingText: { type: String, default: '' },
		defaultAction: { type: String, default: 'editar' },
		initialSort: { type: String, default: 'id' },
		initialSortDirection: { type: String, default: 'asc' },
		detailPredicate: { type: Function, default: null },
		keyboardNavigation: { type: Boolean, default: false },
		singleSelect: { type: Boolean, default: false },
		showActionBar: { type: Boolean, default: true },
		showOutputActions: { type: Boolean, default: null },
		exportFileName: { type: String, default: '' }
	},
	data() {
		return {
			selecionados: [],
			indiceTeclado: -1,
			navegacaoTecladoAtiva: false,
			ordenacao: { campo: this.initialSort, direcao: this.initialSortDirection }
		}
	},
	computed: {
		temLinhas() {
			return Array.isArray(this.rows) && this.rows.length > 0
		},
		totalRegistros() {
			return this.total || (Array.isArray(this.rows) ? this.rows.length : 0)
		},
		totalPaginas() {
			return this.size <= 0 ? 1 : (Math.ceil(this.totalRegistros / this.size) || 1)
		},
		textoExibindoFinal() {
			if (this.showingText) return this.showingText
			if (!this.totalRegistros) return '0'
			if (!this.showPagination) return this.rows.length ? `1-${this.rows.length}` : '0'
			return `${this.page * this.size + 1}-${Math.min((this.page + 1) * this.size, this.totalRegistros)}`
		},
		colunasVisiveis() {
			return this.gridRenderedColumns()
		},
		linhasOrdenadas() {
			const lista = Array.isArray(this.rows) ? this.rows.slice() : []
			const coluna = this.colunasVisiveis.find((item) => item.key === this.ordenacao.campo)
			if (!coluna || coluna.sortable === false) return lista

			return lista.sort((a, b) => {
				const comparacao = this.compararValores(this.valorOrdenacao(a, coluna), this.valorOrdenacao(b, coluna))
				return this.ordenacao.direcao === 'desc' ? comparacao * -1 : comparacao
			})
		},
		quantidadeSelecionada() {
			return this.linhasSelecionadas.length
		},
		linhasSelecionadas() {
			const selecionados = new Set(this.selecionados)
			return (this.rows || []).filter((item, index) => selecionados.has(this.chaveLinha(item, index)))
		},
		todosSelecionados() {
			return this.linhasOrdenadas.length > 0 && this.linhasOrdenadas.every((item, index) => this.itemSelecionado(item, index))
		},
		algunsSelecionados() {
			return this.linhasOrdenadas.some((item, index) => this.itemSelecionado(item, index)) && !this.todosSelecionados
		},
		acoesVisiveis() {
			return (this.actions || []).filter((acao) => acao && acao.visible !== false)
		},
		mostrarAcoesSaida() {
			if (this.showOutputActions !== null) return this.showOutputActions
			return true
		},
		temTotalizadores() {
			return this.gridTotalizers.length > 0
		},
		colunasTotalizadas() { return this.colunasVisiveis.filter(this.deveTotalizarColuna) },
		indiceRotuloTotalizadores() {
			const indice = this.colunasVisiveis.findIndex(coluna => !this.deveTotalizarColuna(coluna))
			return indice >= 0 ? indice : 0
		}
	},
	watch: {
		selectionEnabled(ativo) { if (!ativo) this.limparSelecao() },
		rows() {
			this.limparSelecao()
			this.indiceTeclado = -1
		},
		selecionados() {
			const rows = this.linhasSelecionadas
			this.$emit('selection-change', { row: rows[0] || null, rows, massSelection: this.selecaoEmMassa, replace: this.selecaoSubstitui })
		}
	},
	methods: {
		chaveLinha(item, index) {
			if (typeof this.rowKey === 'function') return String(this.rowKey(item, index))
			const valor = item ? item[this.rowKey] : null
			if (valor !== null && valor !== undefined && valor !== '') return String(valor)
			return `linha-${index}`
		},
		itemSelecionado(item, index) {
			return this.selecionados.includes(this.chaveLinha(item, index))
		},
		emitirAcao(action, rowsInformadas = null) {
			const rows = Array.isArray(rowsInformadas) ? rowsInformadas : this.linhasSelecionadas
			this.$emit('action', { action, row: rows[0] || null, rows })
		},
		acaoHabilitada(acao) {
			if (!acao) return false
			if (typeof acao.visible !== 'function') return true
			const linhas = this.linhasSelecionadas
			if (linhas.length === 0) return true
			if (acao.single) return linhas.length === 1 && !!acao.visible(linhas[0])
			return linhas.every(linha => !!acao.visible(linha))
		},
		executarAcaoPadrao(item = null, index = null) {
			if (!this.keyboardNavigation || !this.defaultAction) return
			const acao = this.acoesVisiveis.find(item => item.key === this.defaultAction)
			// O duplo clique e o Enter respeitam a mesma restrição do botão da ação.
			if (acao?.single && this.linhasSelecionadas.length > 1) return
			if (item && Number.isInteger(index)) {
				this.selecionarSomenteIndice(index)
				if (!this.acaoHabilitada(acao)) return
				this.emitirAcao(this.defaultAction, [item])
				return
			}
			if (this.linhasSelecionadas.length) {
				if (!this.acaoHabilitada(acao)) return
				this.emitirAcao(this.defaultAction)
			}
		},
		selecionarSomenteIndice(index) {
			if (!this.selectionEnabled) return
			this.versaoSelecao++
			this.selecaoEmMassa = false
			this.selecaoSubstitui = true
			if (!this.linhasOrdenadas.length) return
			const indice = Math.max(0, Math.min(Number(index) || 0, this.linhasOrdenadas.length - 1))
			const item = this.linhasOrdenadas[indice]
			this.indiceTeclado = indice
			this.selecionados = [this.chaveLinha(item, indice)]
			this.$emit('keyboard-highlight', { row: item, index: indice })
			this.$nextTick(() => this.rolarLinhaTecladoAtiva())
		},
		focarNavegacaoTeclado(posicao = 'primeiro') {
			this.navegacaoTecladoAtiva = true
			if (!this.keyboardNavigation || !this.linhasOrdenadas.length) return
			let indice = this.indiceTeclado
			if (posicao === 'ultimo') indice = this.linhasOrdenadas.length - 1
			if (indice < 0 || indice >= this.linhasOrdenadas.length) indice = 0
			this.selecionarSomenteIndice(indice)
			this.$nextTick(() => {
				if (this.$el && typeof this.$el.focus === 'function') this.$el.focus({ preventScroll: true })
				this.rolarLinhaTecladoAtiva()
			})
		},
		moverIndiceTeclado(delta) {
			const total = this.linhasOrdenadas.length
			if (!total) return
			const atual = this.indiceTeclado < 0 ? (delta > 0 ? -1 : 0) : this.indiceTeclado
			this.selecionarSomenteIndice((atual + delta + total) % total)
		},
		tratarNavegacaoTeclado(event) {
			if (!this.selectionEnabled || !this.keyboardNavigation || !event || event.defaultPrevented) return
			const tag = String(event.target?.tagName || '').toUpperCase()
			const controleInterativo = ['BUTTON', 'INPUT', 'SELECT', 'TEXTAREA', 'A'].includes(tag)
			if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
				this.navegacaoTecladoAtiva = true
				event.preventDefault()
				event.stopPropagation()
				this.moverIndiceTeclado(event.key === 'ArrowDown' ? 1 : -1)
				return
			}
			if (event.key === 'Home' || event.key === 'End') {
				this.navegacaoTecladoAtiva = true
				event.preventDefault()
				event.stopPropagation()
				this.selecionarSomenteIndice(event.key === 'Home' ? 0 : this.linhasOrdenadas.length - 1)
				return
			}
			if (event.key === 'Enter' && !controleInterativo) {
				event.preventDefault()
				event.stopPropagation()
				this.executarAcaoPadrao()
			}
		},
		rolarLinhaTecladoAtiva() {
			if (!this.$el || this.indiceTeclado < 0) return
			const linhas = this.$el.querySelectorAll('tbody > tr.cadastro-table-row')
			const linha = linhas[this.indiceTeclado]
			if (linha && typeof linha.scrollIntoView === 'function') linha.scrollIntoView({ block: 'nearest' })
		},
		ordenar(coluna) {
			if (!coluna || coluna.sortable === false) return
			if (this.ordenacao.campo === coluna.key) {
				this.ordenacao.direcao = this.ordenacao.direcao === 'asc' ? 'desc' : 'asc'
				return
			}
			this.ordenacao = { campo: coluna.key, direcao: coluna.sortDirection || 'asc' }
		},
		iconeOrdenacao(coluna) {
			if (!coluna || this.ordenacao.campo !== coluna.key) return 'fas fa-sort cadastro-sort-icon'
			return this.ordenacao.direcao === 'asc' ? 'fas fa-sort-up cadastro-sort-icon-active' : 'fas fa-sort-down cadastro-sort-icon-active'
		},
		classeCabecalho(coluna, index = 0) {
			return [
				'cadastro-table-sort',
				index === 0 ? 'cadastro-table-first-data' : '',
				this.colunaPrincipal(coluna) ? 'cadastro-table-main-data' : '',
				coluna.headerClass || '',
				coluna.class || '',
				coluna.align ? `text-${coluna.align}` : ''
			]
		},
		classeCelula(coluna, index = 0) {
			return [
				index === 0 ? 'cadastro-table-first-data' : '',
				this.colunaPrincipal(coluna) ? 'cadastro-table-main-data' : '',
				coluna.class || '',
				coluna.cellClass || '',
				coluna.align ? `text-${coluna.align}` : ''
			]
		},
		colunaPrincipal(coluna) {
			const classes = `${coluna?.class || ''} ${coluna?.cellClass || ''} ${coluna?.headerClass || ''}`
			return classes.includes('cadastro-col-principal')
		},
		classeBotaoAcao(acao) {
			const variantes = {
				success: 'btn-success text-white',
				warning: 'btn-warning text-dark',
				danger: 'btn-danger text-white',
				primary: 'btn-primary text-white',
				refinance: 'btn-action-refinance text-white',
				edit: 'btn-action-edit',
				delete: 'btn-action-delete',
				view: 'btn-action-view',
				preview: 'btn-action-preview',
				add: 'btn-action-add',
				save: 'btn-action-save',
				back: 'btn-action-back'
			}
			if (acao?.variant && variantes[acao.variant]) return variantes[acao.variant]

			const texto = `${acao?.key || ''} ${acao?.label || ''} ${acao?.icon || ''}`.toLowerCase()
			if (/(excluir|remover|cancelar|delete|trash|times-circle)/.test(texto)) return variantes.delete
			if (/(pré-visualizar|pre-visualizar|previsualizar|file-search)/.test(texto)) return variantes.preview
			if (/(visualizar|view|eye)/.test(texto)) return variantes.view
			if (/(editar|alterar|edit|pen|pencil)/.test(texto)) return variantes.edit
			if (/(adicionar|incluir|novo|selecionar|confirmar|add|plus|check)/.test(texto)) return variantes.add
			if (/(salvar|save|rascunho)/.test(texto)) return variantes.save
			if (/(voltar|back|arrow-left)/.test(texto)) return variantes.back
			return 'btn-light'
		},
		valorCelula(item, coluna) {
			if (typeof coluna.value === 'function') return coluna.value(item)
			return item ? item[coluna.key] : null
		},
		valorOrdenacao(item, coluna) {
			if (typeof coluna.sortValue === 'function') return coluna.sortValue(item)
			return this.valorCelula(item, coluna)
		},
		formatarValorCelula(item, coluna) {
			const valor = this.valorCelula(item, coluna)
			if (typeof coluna.format === 'function') return coluna.format(valor, item)
			if (valor === null || valor === undefined || valor === '') return '-'
			return valor
		},
		deveTotalizarColuna(coluna = {}) {
			if (coluna.totalize === true) return true
			if (coluna.totalize === false || coluna.visible === false) return false
			const texto = `${coluna.key || ''} ${coluna.label || ''}`.replace(/([a-z0-9])([A-Z])/g, '$1 $2').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
			if (/(^|\s|_)(id|codigo|numero|documento|sequencia|seq|status|parcela|pagina|percentual|taxa|margem)(\s|_|$)/.test(texto)) return false
			return /(valor|total|saldo|preco|custo|desconto|acrescimo|encargo|pago|recebido|credito|debito|quantidade|\bqtd\b|peso)/.test(texto)
		},
		totalColuna(coluna) {
			if (typeof coluna.totalValue === 'function') return Number(coluna.totalValue(this.linhasOrdenadas) || 0)
			return this.linhasOrdenadas.reduce((total, item) => {
				const valor = this.normalizarNumero(this.valorCelula(item, coluna))
				return total + (Number.isNaN(valor) ? 0 : valor)
			}, 0)
		},
		formatarTotalColuna(coluna) {
			const total = this.totalColuna(coluna)
			if (typeof coluna.totalFormat === 'function') return coluna.totalFormat(total, this.linhasOrdenadas)
			if (typeof coluna.format === 'function') return coluna.format(total)
			return total.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
		},
		compararValores(valorA, valorB) {
			if (valorA == null || valorA === '') return valorB == null || valorB === '' ? 0 : 1
			if (valorB == null || valorB === '') return -1

			const dataA = new Date(valorA).getTime()
			const dataB = new Date(valorB).getTime()
			if (!Number.isNaN(dataA) && !Number.isNaN(dataB) && String(valorA).match(/\d{4}-\d{2}-\d{2}/)) {
				return dataA - dataB
			}

			const numeroA = this.normalizarNumero(valorA)
			const numeroB = this.normalizarNumero(valorB)
			if (!Number.isNaN(numeroA) && !Number.isNaN(numeroB)) return numeroA - numeroB

			return String(valorA).localeCompare(String(valorB), 'pt-BR', { numeric: true, sensitivity: 'base' })
		},
		normalizarNumero(valor) {
			if (valor === null || valor === undefined || valor === '') return NaN
			if (typeof valor === 'number') return valor
			const texto = String(valor).trim().replace(/[^\d,.-]/g, '')
			if (texto.includes(',')) return parseFloat(texto.replace(/\./g, '').replace(',', '.'))
			return parseFloat(texto)
		},
		nomeArquivoExportacao() {
			const base = this.exportFileName || this.title || 'grade'
			return String(base).normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9_-]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'grade'
		},
		escaparHtml(valor) {
			return String(valor ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;')
		},
		valorSaida(item, coluna) {
			const valor = this.formatarValorCelula(item, coluna)
			if (valor === null || valor === undefined) return ''
			if (typeof valor === 'object') return JSON.stringify(valor)
			return String(valor)
		},
		tabelaSaida() {
			const colunas = this.colunasVisiveis.filter(coluna => coluna.exportable !== false)
			const cabecalho = colunas.map(coluna => `<th>${this.escaparHtml(coluna.label)}</th>`).join('')
			const corpo = this.linhasOrdenadas.map(item => `<tr>${colunas.map(coluna => `<td style="mso-number-format:'\\@'">${this.escaparHtml(this.valorSaida(item, coluna))}</td>`).join('')}</tr>`).join('')
			const totaisExportaveis = this.gridTotalizers.filter(total => this.columns.some(column => String(column.key) === total.field && column.exportable !== false))
			const totais = totaisExportaveis.length ? `<tfoot><tr><td colspan="${Math.max(1, colunas.length)}">${totaisExportaveis.map(total => this.escaparHtml(total.label + ': ' + total.formatted)).join(' · ')}</td></tr></tfoot>` : ''
			return `<table><thead><tr>${cabecalho}</tr></thead><tbody>${corpo}</tbody>${totais}</table>`
		},
		exportarExcel() {
			if (!this.temLinhas) return
			const conteudo = `\ufeff<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="UTF-8"><style>table{border-collapse:collapse}th,td{border:1px solid #cbd5e1;padding:6px 9px;text-align:left}th{background:#e8eef7;font-weight:700}</style></head><body><h3>${this.escaparHtml(this.title)}</h3>${this.tabelaSaida()}</body></html>`
			const url = URL.createObjectURL(new Blob([conteudo], { type: 'application/vnd.ms-excel;charset=utf-8;' }))
			const link = document.createElement('a')
			link.href = url
			link.download = `${this.nomeArquivoExportacao()}.xls`
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
			URL.revokeObjectURL(url)
		},
		imprimirGrid() {
			if (!this.temLinhas) return
			const janela = window.open('', '_blank', 'width=1200,height=760')
			if (!janela) return
			janela.document.open()
			janela.document.write(`<!doctype html><html><head><meta charset="UTF-8"><title>${this.escaparHtml(this.title)}</title><style>@page{size:landscape;margin:10mm}body{font-family:Arial,sans-serif;color:#172033;font-size:10px}h2{font-size:16px;margin:0 0 4px}.resumo{color:#64748b;margin-bottom:12px}table{border-collapse:collapse;width:100%}th,td{border:1px solid #cbd5e1;padding:5px 7px;text-align:left}th{background:#e8eef7}tr:nth-child(even){background:#f8fafc}</style></head><body><h2>${this.escaparHtml(this.title)}</h2><div class="resumo">${this.escaparHtml(`Exibindo ${this.textoExibindoFinal} de ${this.totalRegistros} registro(s)`)}</div>${this.tabelaSaida()}</body></html>`)
			janela.document.close()
			janela.focus()
			janela.print()
		},
		deveExibirDetalhe(item, index) {
			if (!this.$scopedSlots['row-detail']) return false
			if (!this.detailPredicate) return true
			return this.detailPredicate(item, index)
		}
	}
}

</script>

<style scoped>
.cadastro-grid-display-only .cadastro-table tbody tr { cursor: default; }
.cadastro-grid-shell {
	display: flex;
	flex-direction: column;
	height: calc(100dvh - 300px);
	min-height: 210px;
}

.cadastro-grid-keyboard {
	outline: none;
}

.btn-action-refinance {
	background: #f28c28;
	border-color: #dc7714;
}

.btn-action-refinance:hover,
.btn-action-refinance:focus {
	background: #dc7714;
	border-color: #c6670c;
	color: #fff;
}

.cadastro-table-card {
	display: flex;
	flex: 1 1 auto;
	flex-direction: column;
	min-height: 0;
	overflow: visible;
}

.cadastro-table-card .card-body {
	display: flex;
	flex: 1 1 auto;
	flex-direction: column;
	min-height: 0;
}

.cadastro-grid-footer-output-actions {
	align-items: center;
	display: flex;
	gap: .3rem;
}

.cadastro-grid-output-button {
	align-items: center;
	border: 1px solid #d4dbe5;
	border-radius: 5px;
	display: inline-flex;
	font-size: 15px;
	height: 32px;
	justify-content: center;
	padding: 0;
	width: 34px;
}

.cadastro-grid-output-button--excel { background: #edf8f1; color: #178348; }
.cadastro-grid-output-button--excel:hover,.cadastro-grid-output-button--excel:focus { background: #178348; border-color: #178348; color: #fff; }
.cadastro-grid-output-button--print { background: #f7f8fa; color: #536174; }
.cadastro-grid-output-button--print:hover,.cadastro-grid-output-button--print:focus { background: #536174; border-color: #536174; color: #fff; }
.cadastro-grid-output-button:disabled { opacity: .45; }

.cadastro-grid-summary-footer {
	align-items: center;
	background: #fff;
	border: 1px solid #dfe4eb;
	border-radius: 4px;
	box-shadow: 0 2px 7px rgba(15, 23, 42, .07);
	display: flex;
	flex: 0 0 auto;
	gap: .6rem;
	justify-content: space-between;
	margin-top: 7px;
	min-height: 40px;
	overflow: visible;
	padding: 5px 10px;
	position: relative;
	z-index: 4;
}

.cadastro-grid-summary-text {
	align-items: center;
	display: flex;
	flex-wrap: wrap;
	gap: .35rem 1rem;
	min-width: 0;
}

.cadastro-grid-summary-text strong,
.cadastro-grid-summary-text span {
	font-size: 11px;
	white-space: nowrap;
}

.cadastro-grid-summary-text span { color: #64748b; }

.cadastro-grid-summary-pagination {
	align-items: center;
	display: flex;
	flex-wrap: nowrap;
	gap: .8rem;
}

.cadastro-pagination-size,
.cadastro-pagination-nav {
	align-items: center;
	display: flex;
	flex-wrap: nowrap;
	gap: .35rem;
	white-space: nowrap;
}

.cadastro-pagination-size span,
.cadastro-pagination-label {
	color: #64748b;
	font-size: 11px;
	font-weight: 700;
}

.cadastro-pagination-nav .btn {
	align-items: center;
	display: inline-flex;
	height: 30px;
	justify-content: center;
	min-width: 32px;
	padding: .2rem .45rem;
}

.cadastro-table-scroll {
	flex: 1 1 auto;
	min-height: 70px;
	overflow: auto;
}

.cadastro-table-empty {
	align-items: center;
	display: flex;
	flex-direction: column;
	justify-content: center;
}

.cadastro-table {
	font-size: 12px;
	min-width: 100%;
	table-layout: auto;
	width: max-content;
}

.cadastro-table thead th {
	background-color: var(--color-theme);
	border-bottom: 1px solid color-mix(in srgb, var(--color-theme) 72%, #000);
	box-shadow: 0 2px 5px rgba(15, 23, 42, 0.18);
	color: #fff;
	font-size: 10px;
	font-weight: 800;
	letter-spacing: .45px;
	position: sticky;
	text-shadow: 0 1px 1px rgba(0, 0, 0, .18);
	text-transform: uppercase;
	top: 0;
	z-index: 2;
	white-space: nowrap;
}

.cadastro-table tbody td {
	border-color: #eeeeee;
	vertical-align: middle;
	white-space: nowrap;
}

.cadastro-table tbody tr {
	cursor: pointer;
}

.cadastro-table > tbody > tr.cadastro-table-row-alt > td {
	background-color: #f2f4f7;
}

.cadastro-table > tbody > tr:not(.table-active):hover > td {
	background-color: #f2f0ff;
}

.cadastro-table > tbody > tr.table-active > td {
	background-color: rgba(98, 54, 255, 0.1);
}

.cadastro-totalizadores-dock { align-items:stretch; background:#f7f9fc; border-top:1px solid #d8e0ea; display:flex; flex:0 0 auto; min-height:32px; overflow-x:auto; padding:0 8px; position:relative; z-index:3; }
.cadastro-totalizadores-dock__label,.cadastro-totalizadores-dock__item { align-items:center; border-right:1px solid #e2e7ee; display:flex; flex:0 0 auto; gap:.4rem; padding:4px 10px; white-space:nowrap; }
.cadastro-totalizadores-dock__label { color:#64748b; font-size:9px; font-weight:800; letter-spacing:.35px; text-transform:uppercase; }
.cadastro-totalizadores-dock__item small { color:#64748b; font-size:9px; text-transform:uppercase; }
.cadastro-totalizadores-dock__item strong { color:#172033; font-size:11px; font-weight:800; }

.cadastro-table > tbody > tr.cadastro-keyboard-active > td:first-child { position: relative; }
.cadastro-table > tbody > tr.cadastro-keyboard-active > td:first-child::before {
	content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 3px;
	background: var(--color-theme); pointer-events: none;
}

.cadastro-table-check {
	max-width: 42px;
	min-width: 42px;
	width: 42px;
}

.cadastro-table-sort {
	user-select: none;
}

.cadastro-table-first-data {
	padding-left: 12px !important;
}

.cadastro-table-first-data:not(.cadastro-table-main-data) {
	width: 1%;
}

.cadastro-table th:not(.cadastro-table-main-data):not(.cadastro-table-check),
.cadastro-table td:not(.cadastro-table-main-data):not(.cadastro-table-check) {
	width: 1%;
}

.cadastro-table-main-data {
	width: 100%;
}

.cadastro-table-sort i {
	color: rgba(255, 255, 255, .62);
	font-size: 10px;
	text-shadow: none;
	vertical-align: middle;
}

.cadastro-table-sort i.cadastro-sort-icon-active {
	color: #fff;
	filter: drop-shadow(0 1px 1px rgba(0, 0, 0, .28));
}

.cadastro-col-principal {
	min-width: 260px;
	white-space: normal !important;
}

.cadastro-table-enhance {
	border-radius: 3px;
	box-shadow: 0 6px 18px rgba(0, 0, 0, 0.16);
	flex: 0 0 auto;
	margin-bottom: 0;
	margin-top: 8px;
	min-height: 38px;
	padding: 8px 14px;
	position: relative;
	z-index: 4;
}

.cadastro-table-enhance .btn {
	min-width: 90px;
}

.cadastro-table-enhance .btn:disabled {
	opacity: 0.55;
}

::v-deep .cadastro-page-size-select {
	min-width: 86px;
	width: 86px;
}

::v-deep .cadastro-page-size-select.vs--open {
	z-index: 8;
}

::v-deep .cadastro-page-size-select .vs__dropdown-toggle {
	align-items: center;
	min-height: 32px;
	padding: 0 8px;
}

::v-deep .cadastro-page-size-select .vs__selected-options {
	align-items: center;
	flex-wrap: nowrap;
	min-width: 0;
	padding: 0;
}

::v-deep .cadastro-page-size-select .vs__selected {
	font-size: 12px;
	font-weight: 400;
	line-height: 1.2;
	margin: 0;
	padding: 0;
}

::v-deep .cadastro-page-size-select .vs__search,
::v-deep .cadastro-page-size-select .vs__search:focus {
	font-size: 12px;
	margin: 0;
	padding: 0;
}

::v-deep .cadastro-page-size-select .vs__actions {
	padding: 0 0 0 6px;
}

::v-deep .cadastro-page-size-select.vs--open .vs__dropdown-menu {
	border-bottom: 0;
	border-radius: 4px 4px 0 0;
	border-top: 1px solid rgba(60, 60, 60, 0.26);
	bottom: calc(100% - 1px);
	left: 0;
	max-height: 180px !important;
	min-width: 86px;
	top: auto;
	width: 86px;
	z-index: 9;
}

.cadastro-row-detail > td {
	background-color: #fbfbfb;
	cursor: default;
}

@media (max-width: 767px) {
	.cadastro-grid-shell {
		height: calc(100dvh - 330px);
		min-height: 220px;
	}

	.cadastro-col-principal {
		min-width: 210px;
	}

	.cadastro-table-enhance .btn {
		flex: 1 1 calc(50% - 8px);
		min-width: 0;
	}

	.cadastro-grid-summary-footer {
		align-items: flex-start;
		flex-direction: column;
		justify-content: flex-start;
	}

	.cadastro-grid-summary-pagination {
		overflow-x: auto;
		width: 100%;
	}
}
</style>
